import { useEffect, useRef, useState } from "react";
import { sfx } from "../../lib/sound";
import { DEVICE_LABEL } from "../../lib/console";
import { LockBrief } from "./LockBrief";

const FRAGMENT_MS = 7000;
const BRIEF_MS = 12000;
const HOLD_MS = 3000;

// A fragment flashes, fades; then the hive asks for a number, set on a dial and locked in by holding it still.
export const LockRiddle = ({ riddle, dial, dialValue, giveIn, lastEvent, showHint, attempt, onProgress, onResult }) => {
  const [phase, setPhase] = useState("brief");
  const [left, setLeft] = useState(FRAGMENT_MS);
  const [moved, setMoved] = useState(false);
  const [holdKey, setHoldKey] = useState(0);
  const [hold, setHold] = useState(0);
  const seen = useRef(null);
  const value = dialValue ?? 5;
  const valueRef = useRef(value);
  valueRef.current = value;
  const phaseRef = useRef(phase);
  phaseRef.current = phase;

  const toQuestion = () => { setPhase("question"); setMoved(false); setHold(0); onProgress(0.5); };
  const lockIn = () => { setPhase("done"); onResult(valueRef.current === riddle.answer); };

  const start = () => {
    if (riddle.mode !== "fragment") { toQuestion(); return; }
    setPhase("fragment");
    setLeft(FRAGMENT_MS);
    onProgress(0.25);
  };

  useEffect(() => { if (attempt > 1) toQuestion(); }, [attempt]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (phase !== "fragment") return undefined;
    const started = Date.now();
    const t = setInterval(() => {
      const remaining = Math.max(0, FRAGMENT_MS - (Date.now() - started));
      setLeft(remaining);
      if (remaining === 0) { clearInterval(t); toQuestion(); }
    }, 100);
    return () => clearInterval(t);
  }, [phase]); // eslint-disable-line react-hooks/exhaustive-deps

  // Dwell confirm: once the dial has moved, holding it still for HOLD_MS locks the answer in.
  useEffect(() => {
    if (phase !== "question" || !moved) return undefined;
    const started = Date.now();
    setHold(0);
    const t = setInterval(() => {
      const p = Math.min(1, (Date.now() - started) / HOLD_MS);
      setHold(p);
      if (p >= 1) { clearInterval(t); if (phaseRef.current === "question") lockIn(); }
    }, 100);
    return () => clearInterval(t);
  }, [phase, moved, holdKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!lastEvent || lastEvent === seen.current) return;
    seen.current = lastEvent;
    if (phase === "brief") { if (lastEvent.type === "press" || lastEvent.type === "confirm") start(); return; }
    if (lastEvent.type === "dial" && lastEvent.id === dial?.id) {
      sfx.tick();
      if (phase === "question") { setMoved(true); setHoldKey((k) => k + 1); }
      return;
    }
    if (lastEvent.type === "confirm") {
      if (phase === "fragment") toQuestion();
      else if (phase === "question") lockIn();
    }
  }, [lastEvent]); // eslint-disable-line react-hooks/exhaustive-deps

  if (phase === "brief") {
    const dialLine = dial ? `Turn ${dial.name} (${DEVICE_LABEL[dial.device]}) to your answer and hold it still for 3 seconds to lock it in.` : "Set your answer, then the operator confirms with Enter.";
    return (
      <div data-testid="lock-riddle" data-phase={phase}>
        <LockBrief
          title="Recall the fragment"
          ms={BRIEF_MS}
          onDone={start}
          lines={[
            riddle.mode === "fragment" ? "A fragment of this memory surfaces for 7 seconds. Hold it in mind - it fades." : "The hive tests a memory you have already recovered.",
            "Then the hive asks one question. The answer is a number from 1 to 10.",
            dialLine,
            `Wrong answer: try again. After two misses you get a hint; after ${giveIn} the hive lets the memory go.`,
          ]}
        />
      </div>
    );
  }

  return (
    <div data-testid="lock-riddle" data-phase={phase} className="grid gap-5">
      {phase === "fragment" ? (
        <div data-testid="riddle-fragment" className="border border-cyan-400/30 bg-cyan-400/5 p-6">
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-cyan-300">A fragment surfaces · hold it in mind</p>
          <p className="mt-4 font-display text-2xl leading-snug text-slate-100 sm:text-3xl">{riddle.fragment}</p>
          <div className="mt-5 h-1 w-full bg-slate-800"><div className="h-full bg-cyan-300 transition-[width] duration-100" style={{ width: `${(left / FRAGMENT_MS) * 100}%` }} /></div>
        </div>
      ) : (
        <div className="grid gap-5">
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-cyan-300">{riddle.mode === "recall" ? `The hive tests what you already recovered · ${riddle.sourceTitle}` : "The fragment is gone. The hive asks:"}</p>
            <p data-testid="riddle-question" className="mt-3 font-display text-2xl leading-snug text-slate-100 sm:text-3xl">{riddle.question}</p>
            {showHint && <p data-testid="riddle-hint" className="mt-3 text-sm text-amber-200">Hint · {riddle.hint}</p>}
          </div>
          {dial && (
            <div data-testid="riddle-dial" className="grid gap-3 border border-slate-800 bg-slate-950/70 p-5">
              <p className="font-mono text-sm uppercase tracking-widest text-slate-300">
                Turn <span className="text-amber-200">{dial.name}</span> <span className="text-slate-500">({DEVICE_LABEL[dial.device]})</span> to your answer · hold it still to lock in
              </p>
              <div className="flex items-end gap-1.5">
                {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => (
                  <div key={n} data-testid={`dial-seg-${n}`} data-active={n === value} className={`flex-1 border text-center font-mono text-xs transition-all duration-150 ${n === value ? "border-amber-300 bg-amber-300/30 text-white" : n < value ? "border-amber-300/30 bg-amber-300/10 text-amber-200/60" : "border-slate-800 text-slate-600"}`} style={{ height: `${18 + n * 3}px`, lineHeight: `${18 + n * 3}px` }}>{n}</div>
                ))}
              </div>
              <div className="flex items-center gap-4">
                <p data-testid="dial-value" className="font-display text-5xl text-amber-200">{value}</p>
                <div className="flex-1">
                  <p data-testid="dial-hold-label" className="font-mono text-[11px] uppercase tracking-widest text-slate-400">{moved ? (hold >= 1 ? "Locked in" : "Hold still… locking in") : "Turn the dial · then hold it still for 3 seconds"}</p>
                  <div className="mt-1 h-1.5 w-full bg-slate-800"><div data-testid="dial-hold" className="h-full bg-emerald-300 transition-[width] duration-100" style={{ width: `${hold * 100}%` }} /></div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
